import {
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  GoalSortingMode,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import {
  filterGoals,
  filterGoalsByFilterDocument,
  filterLocalizedLifeAreas,
  getGoalListOrder,
  getWeekStartsOn,
} from '@griply/utils';
import {
  useMergeGoalLinkChanges,
  useSortedGoals,
  useSubGoals,
  useTimelineDropReorder,
  useUpdateGoal,
} from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTimelineContainer } from 'shared/components/ui/timeline';
import { useToday } from 'shared/contexts/today';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { Goal, GoalReorderSectionUpdate, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import {
  TimelineScrollRequest,
  TimelineTimeframes,
} from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';

import { resolveRoadmapTimelineReorder } from './resolve-roadmap-timeline-reorder';
import * as Styled from './roadmap-timeline.style';

export type RoadmapTimelineProps = {
  goal: Goal;
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  scrollRequest: TimelineScrollRequest;
  hideCompleted?: boolean;
};

export const RoadmapTimeline: React.FC<RoadmapTimelineProps> = ({
  goal,
  timeframe,
  referenceDate,
  scrollRequest,
  hideCompleted,
}) => {
  const links = useEntityLinks();
  const { t } = useTranslation();
  const user = useUser();
  const today = useToday();
  const weekStartsOn = getWeekStartsOn(user);
  const subGoals = useSubGoals(goal.id, true);
  const allGoals = useSortedGoals();
  const allLifeAreas = useSortedLocalizedLifeAreas();
  const { submit: updateGoal } = useUpdateGoal();

  // The focal goal always renders as the root bar, even when completed or
  // excluded by a filter; archived descendants never show on a timeline. The
  // per-goal filters (goal.goalFilterDocument) narrow descendants only —
  // matching the list, which merges the same document via
  // getSubGoalsGoalFilterDocument.
  const goals = useMemo(() => {
    const base = filterGoals(subGoals, {
      links,
      hideArchived: true,
      hideCompleted: !!hideCompleted,
    });
    const descendants = filterGoalsByFilterDocument(
      base,
      goal.goalFilterDocument ?? undefined,
      { today, weekStartsOn, links },
    );
    return [goal, ...descendants];
  }, [goal, subGoals, hideCompleted, today, weekStartsOn, links]);

  const lifeAreas = useMemo(
    () =>
      filterLocalizedLifeAreas(allLifeAreas, {
        goals,
        showEmptyLifeAreas: false,
        allGoals,
        links,
      }),
    [allLifeAreas, goals, allGoals, links],
  );

  const customOrder = useMemo(
    () =>
      getGoalListOrder({
        goals: subGoals,
        allGoals,
        sortMode: goal.goalSortingMode ?? GoalSortingMode.Custom,
        sortDirection: user?.settings?.goalSortDirection ?? OrderDirection.Asc,
        goalSorting: goal.goalSorting,
        links,
      }),
    [subGoals, allGoals, goal, user, links],
  );

  const handleChangeGoal = useCallback(
    (
      id: ID,
      { startDate, deadline }: { startDate?: Timestamp; deadline?: Timestamp },
    ) => {
      const update: UpdateGoal = { id };
      if (startDate) {
        update.startDate = startDate;
      }
      if (deadline) {
        update.deadline = deadline;
      }
      updateGoal(update);
    },
    [updateGoal],
  );

  const mergeGoalLinkChanges = useMergeGoalLinkChanges();

  const handleReorder = useCallback(
    (
      updatedSections: GoalReorderSectionUpdate[],
      updatedGoals: UpdateGoal[],
      goalLifeAreaChanges?: GoalLifeAreaLinkChange[],
      goalParentChanges?: GoalParentLinkChange[],
    ) => {
      const resolved = resolveRoadmapTimelineReorder(
        goal.id,
        updatedSections,
        updatedGoals,
      );

      // An empty result is a drop this timeline rejects — writing its link changes anyway
      // would apply half of a move the user is not getting.
      if (!resolved.length) {
        return;
      }

      mergeGoalLinkChanges(resolved, {
        goalLifeAreaChanges,
        goalParentChanges,
      }).forEach((update) => updateGoal(update));
    },
    [goal.id, updateGoal, mergeGoalLinkChanges],
  );

  const onReorderDrop = useTimelineDropReorder(handleReorder);
  const rootLink = useMemo(() => ({ goalId: goal.id }), [goal.id]);

  const handleChangeCollapsedItems = useCallback(
    (ids: ID[]) => updateGoal({ id: goal.id, collapsedSubGoals: ids }),
    [goal.id, updateGoal],
  );

  return (
    <Styled.Container>
      <GoalTimelineContainer
        links={links}
        timeframe={timeframe}
        referenceDate={referenceDate}
        scrollRequest={scrollRequest}
        goals={goals}
        allGoals={allGoals}
        lifeAreas={lifeAreas}
        weekStartsOn={weekStartsOn}
        groupBy={GroupByMode.None}
        customOrder={customOrder}
        rootLink={rootLink}
        onChangeGoal={handleChangeGoal}
        onReorderDrop={onReorderDrop}
        onChangeCollapsedItems={handleChangeCollapsedItems}
        collapsedItems={goal.collapsedSubGoals ?? undefined}
        title={t('pages.goal-detail.tabs.roadmap.label')}
      />
    </Styled.Container>
  );
};
